import { MemberTableSubject } from "@components/common/faro-table/faro-table-types";
import {
  DEFAULT_MEMBERS_SORT_BY,
  DEFAULT_TEAM_MEMBERS_SORT_BY,
} from "@components/table/members/members-sort-constants";
import { MembersSortField } from "@components/table/members/members-table-constants";
import { BaseMemberProps, MembersSortBy } from "@custom-types/member-types";
import { isMembersSortField } from "@custom-types/type-guards";
import { isProjectMemberBase } from "@custom-types/member-type-guards";
import { GridSortModel } from "@mui/x-data-grid-pro";
import { SdbMemberTypes } from "@custom-types/member-types";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { getMembersSortBy, getTeamMembersSortBy } from "@utils/member-utils";
import { convertGridSortModelToSortBy } from "@utils/sort-utils";
import { isTeam } from "@utils/team-utils";
import { isMemberActive } from "@utils/user-utils";
import { FaroCoreApiTypes } from "@stellar/core-api-client";

/** All the possible headers for members table */
export enum MemberHeaders {
  /** User avatar */
  avatar = "Avatar",

  /** User full name */
  user = "User",

  /** User full name or team name */
  name = "Name",

  /** User email */
  email = "Email",

  /** User status */
  status = "Status",

  /** User role */
  role = "Role",

  /** Date in which a member was added to a group */
  joinedDate = "Added date",

  /** Any other possible options */
  options = "Options",
}

/** Returns the specific role column header according to the table context type */
export function getRoleHeader(
  tableSubject: MemberTableSubject | undefined
): string {
  switch (tableSubject) {
    case "workspace":
    case "activeMembers":
      return "Workspace Role";

    case "project":
      return "Project Role";

    default:
      return "Role";
  }
}

/** The display name to show representing memberTableSubject */
export function getMemberTableSubjectDisplayName(
  memberTableSubject: MemberTableSubject
): string {
  switch (memberTableSubject) {
    case "project":
    case "group":
    case "workspace":
    case "snapshot":
      return memberTableSubject;

    case "team":
      return TEAM_DISPLAY_NAME;

    case "workspaceTeam":
      return "workspace";
    case "activeMembers":
      return "active members";
  }
}

/** Returns the subject name for Amplitude tracking based on table context */
export function getMemberTableTrackingSubject(
  tableSubject: MemberTableSubject
): string {
  switch (tableSubject) {
    case "workspace":
      return "Workspace Member";
    case "project":
      return "Project Member";
    case "group":
      return "Group Member";
    case "team":
      return "Team Member";
    case "activeMembers":
      return "Active Member";
    default:
      return "Member";
  }
}

/** Normalizes a member name for sorting, returning null when empty. */
function normalizeMemberName(name: string | null | undefined): string | null {
  const trimmedName = name?.trim();
  return trimmedName && trimmedName.length > 0 ? trimmedName : null;
}

/**
 * Compares two members by their normalized name.
 * Empty names are placed last, non-empty names use numeric locale comparison.
 *
 * @param memberA - First member to compare
 * @param memberB - Second member to compare
 * @param order - Sort order: "ascending" (default) or "descending"
 * @returns Negative if memberA < memberB, positive if memberA > memberB, 0 if equal
 */
export function compareMembersByDefaultName(
  memberA: SdbMemberTypes,
  memberB: SdbMemberTypes,
  order: "ascending" | "descending" = "ascending"
): number {
  const nameA = normalizeMemberName(memberA.name);
  const nameB = normalizeMemberName(memberB.name);

  if (!nameA || !nameB) {
    if (!nameA && !nameB) {
      return 0;
    }

    return nameA ? -1 : 1;
  }

  const comparison = nameA.localeCompare(nameB, undefined, {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    numeric: true,
    sensitivity: "base",
  });

  return order === "ascending" ? comparison : -comparison;
}

/**
 * Compares two members by their status for sorting.
 * user-group members (teams) always go to the end.
 * @param memberA - First member to compare
 * @param memberB - Second member to compare
 * @param order - Sort order: "ascending" (active first, then pending) or "descending" (pending first, then active)
 */
export function compareMembersByStatus(
  memberA: SdbMemberTypes,
  memberB: SdbMemberTypes,
  order: "ascending" | "descending"
): number {
  const isATeam = isTeam(memberA);
  const isBTeam = isTeam(memberB);

  // Teams always go to the end
  if (isATeam && !isBTeam) {
    return order === "ascending" ? 1 : -1;
  }
  if (!isATeam && isBTeam) {
    return order === "ascending" ? -1 : 1;
  }
  if (isATeam && isBTeam) {
    return 0;
  }

  // Both have status: sort by active/pending
  const isAActive = isMemberActive(memberA);
  const isBActive = isMemberActive(memberB);
  const comparison = Number(isBActive) - Number(isAActive);
  return order === "ascending" ? comparison : -comparison;
}

/**
 * @returns a valid members `sortBy` option from the passed GridSortModel
 * @param gridSortModel Members table sort model
 */
export function getMembersSortByFromGridSortModel(
  gridSortModel: GridSortModel
): MembersSortBy {
  return convertGridSortModelToSortBy<MembersSortField, MembersSortBy>(
    gridSortModel,
    {
      defaultSortBy: DEFAULT_MEMBERS_SORT_BY,
      isValidSortField: isMembersSortField,
      getSortBy: getMembersSortBy,
    }
  );
}

/**
 * @returns a valid members `sortBy` option from the passed GridSortModel
 * @param gridSortModel Members table sort model
 */
export function getTeamMembersSortByFromGridSortModel(
  gridSortModel: GridSortModel
): FaroCoreApiTypes.TeamMemberSortBy {
  return convertGridSortModelToSortBy<
    MembersSortField,
    FaroCoreApiTypes.TeamMemberSortBy
  >(gridSortModel, {
    defaultSortBy: DEFAULT_TEAM_MEMBERS_SORT_BY,
    isValidSortField: isMembersSortField,
    getSortBy: getTeamMembersSortBy,
  });
}

/**
 * Checks if a specific field is visible for a given table subject.
 * @param field The field to check visibility for.
 * @param tableSubject The table subject context.
 * @returns True if the field is visible, false otherwise.
 */
export function isFieldVisibleForTableSubject(
  field: MembersSortField,
  tableSubject: MemberTableSubject
): boolean {
  const tablesWithUserColumn = new Set<MemberTableSubject>([
    "workspace",
    "activeMembers",
    "team",
  ]);

  /* eslint-disable @typescript-eslint/naming-convention */
  const fieldVisibility: Partial<Record<MembersSortField, boolean>> = {
    user: tablesWithUserColumn.has(tableSubject),
    name: !tablesWithUserColumn.has(tableSubject),
    joinedDate: tableSubject === "group",
  };
  /* eslint-enable @typescript-eslint/naming-convention */

  return fieldVisibility[field] ?? true;
}

interface CanRemoveMemberParams extends BaseMemberProps {
  /** Whether the required IDs for permission checks are available */
  hasRequiredIds: boolean;

  /** Whether the user has permission to remove the member from the project */
  canRemoveFromProject: boolean;

  /** Whether the user has permission to remove the member from the company */
  canDeleteFromCompany: boolean;
}

/** Whether the given member can be removed from the project */
export function canRemoveMember({
  member,
  hasRequiredIds,
  canRemoveFromProject,
  canDeleteFromCompany,
}: CanRemoveMemberParams): boolean {
  if (!hasRequiredIds) {
    return false;
  }
  const isResourceOwner =
    isProjectMemberBase(member) && member.role === "RESOURCE_OWNER";
  return !isResourceOwner && (canRemoveFromProject || canDeleteFromCompany);
}
